import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import type { QuizPlanPaymentResponse } from '@/api/gen/_model/quizPlanPaymentResponse';
import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';

import {
  isValidPlanHashId,
  type PaymentEntry,
} from '../constants/paymentIntent';
import { getPaymentStepFromPath, PAYMENT_URLS } from '../constants/paymentUrls';
import { paymentResultManager } from '../services/paymentResultManager';
import { getPaymentDraftKey, setPaymentDraft } from '../utils/paymentDraft';
import { getPaymentEntryUrl } from '../utils/paymentEntryUrl';

export type PaymentFlowStep =
  | 'pricing'
  | 'methods'
  | 'checkout'
  | 'payment'
  | 'complete';

export type PaymentSource = 'direct' | 'link' | 'redirect';

export type NavigationOptions = {
  /** 'new-tab' | 'same-tab' */
  target?: 'new-tab' | 'same-tab';
  /** 결제 진입 경로 */
  source?: PaymentSource;
  /** processing 재개 시 원래 미래 플랜 변경 대상 */
  targetQuizPlanHashId?: string;
};

type StartPaymentFlowPayload = PaymentEntry & { countryCode?: string };

/**
 * 결제 플로우 네비게이션을 관리하는 간단한 훅
 *
 * 플로우 순서:
 * 1. pricing → checkout/method
 * 2. checkout/method → checkout (plan 선택 가능)
 * 3. checkout → checkout/payment?orderId=xxx
 * 4. checkout/payment → checkout/complete (성공 시)
 */
export function usePaymentFlow() {
  const router = useRouter();
  const openWindow = useOpenWindow();
  const { user } = useUser();
  const ownerId = user?.quizUserId;

  // 현재 경로에서 현재 단계 파악
  const currentStep = useMemo((): PaymentFlowStep => {
    return getPaymentStepFromPath(router.pathname) as PaymentFlowStep;
  }, [router.pathname]);

  // URL query parameter에서 source 파악
  const paymentSource = useMemo((): PaymentSource => {
    return (router.query.source as PaymentSource) || 'link';
  }, [router.query.source]);

  // 네비게이션 헬퍼 함수
  const navigate = useCallback(
    (url: string, options: NavigationOptions = {}) => {
      const { target = 'same-tab' } = options;

      switch (target) {
        case 'new-tab':
          openWindow(url);
          break;
        case 'same-tab':
        default:
          void router.push(url);
          break;
      }
    },
    [openWindow, router],
  );

  // Step 1: 가격 페이지에서 결제 방법 선택 페이지로
  const startPaymentFlow = useCallback(
    (
      { countryCode, ...entry }: StartPaymentFlowPayload,
      options?: NavigationOptions,
    ) => {
      navigate(getPaymentEntryUrl(entry, countryCode), options);
    },
    [navigate],
  );

  // Step 2: 결제 방법 선택 페이지에서 체크아웃 페이지로
  const goToCheckout = useCallback(
    ({
      entry,
      formData,
      ...options
    }: {
      entry: PaymentEntry;
      formData: Record<string, unknown>;
    } & NavigationOptions) => {
      setPaymentDraft(getPaymentDraftKey(entry), ownerId, formData);
      navigate(getPaymentEntryUrl(entry), options);
    },
    [navigate, ownerId],
  );

  // Step 3: 체크아웃에서 결제 페이지로 (orderId 필요)
  const goToPayment = useCallback(
    (preOrderId: string, options: NavigationOptions = {}) => {
      if (!preOrderId) {
        console.error('주문 preOrderId 가 없습니다');
        return;
      }

      const source: PaymentSource = 'direct';
      const params = new URLSearchParams({ source });
      const target = options.targetQuizPlanHashId ?? router.query.target;
      const targetQuizPlanHashId = isValidPlanHashId(target)
        ? target
        : undefined;
      if (targetQuizPlanHashId) {
        params.set('target', targetQuizPlanHashId);
      }
      const url = `${PAYMENT_URLS.getPaymentUrl(preOrderId)}&${params}`;
      navigate(url, options);
    },
    [navigate, router],
  );

  // Step 4: 결제 완료 페이지로 이동 (PaymentCompleteData와 함께)
  const goToComplete = useCallback(
    (paymentDataRes: QuizPlanPaymentResponse) => {
      const url = PAYMENT_URLS.COMPLETE;
      const paymentData = paymentResultManager.transformData(paymentDataRes);
      const storageKey = paymentResultManager.save(paymentData);

      void router.push({
        pathname: url,
        query: { key: storageKey },
      });
    },
    [router],
  );

  // 플로우 진행도 계산 (0-100%)
  const progress = useMemo(() => {
    const steps: PaymentFlowStep[] = [
      'pricing',
      'methods',
      'checkout',
      'payment',
      'complete',
    ];
    const currentIndex = steps.indexOf(currentStep);
    return ((currentIndex + 1) / steps.length) * 100;
  }, [currentStep]);

  return {
    // 현재 상태
    currentStep,
    progress,
    paymentSource,

    // 네비게이션 함수
    startPaymentFlow,
    goToCheckout,
    goToPayment,
    goToComplete,
    navigate, // 범용 네비게이션 함수 노출

    // 현재 단계 체크 헬퍼
    isPricingStep: currentStep === 'pricing',
    isMethodsStep: currentStep === 'methods',
    isCheckoutStep: currentStep === 'checkout',
    isPaymentStep: currentStep === 'payment',
    isCompleteStep: currentStep === 'complete',
  };
}
